import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GurujiChatWidget from '../components/GurujiChatWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Keyboard, Clock, Target, Loader2, TrendingUp, Award, Play } from 'lucide-react';
import {
  upsertMetaByName,
  upsertCanonical,
  upsertJsonLd,
  removeJsonLd
} from '../lib/seoHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://vidya365.in';

const EXAMS = [
  { id: 'ssc-cgl', name: 'SSC CGL', accent: 'bg-red-600' },
  { id: 'ssc-chsl', name: 'SSC CHSL', accent: 'bg-blue-600' },
  { id: 'ibps-clerk', name: 'IBPS Clerk', accent: 'bg-green-600' },
  { id: 'rrb-ntpc', name: 'RRB NTPC', accent: 'bg-amber-600' }
];

const TypingTestList = () => {
  const [passages, setPassages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeExam, setActiveExam] = useState('ssc-cgl');
  const [activeLang, setActiveLang] = useState('English');

  useEffect(() => {
    document.title = 'Free Typing Test for SSC CGL, CHSL, IBPS Clerk, RRB NTPC | Vidya365';
    upsertMetaByName('description', 'Free online typing test for SSC CGL, SSC CHSL, IBPS Clerk and RRB NTPC. Real exam pattern, 10-minute tests, instant WPM & accuracy results. English & Hindi.');
    upsertMetaByName('keywords', 'SSC CGL typing test, SSC CHSL typing test, IBPS Clerk typing test, RRB NTPC typing test, online typing test free, WPM test, Hindi typing test, English typing practice, government exam typing');
    upsertCanonical(`${SITE_URL}/typing-test`);
    upsertJsonLd('typing-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Typing Test', item: `${SITE_URL}/typing-test` }
      ]
    });
    fetchPassages();
    fetchStats();
    return () => removeJsonLd('typing-breadcrumb');
  }, []);

  const fetchPassages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/typing/passages`);
      const data = res.ok ? await res.json() : [];
      setPassages(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('studentToken');
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/typing/stats/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setStats(await res.json());
    } catch (e) { /* noop */ }
  };

  const filtered = useMemo(
    () => passages.filter(p => p.exam_id === activeExam && p.language === activeLang),
    [passages, activeExam, activeLang]
  );

  const availableLangs = useMemo(() => {
    const langs = new Set(passages.filter(p => p.exam_id === activeExam).map(p => p.language));
    return Array.from(langs);
  }, [passages, activeExam]);

  // Auto-switch if current language not available for selected exam
  useEffect(() => {
    if (availableLangs.length && !availableLangs.includes(activeLang)) {
      setActiveLang(availableLangs[0]);
    }
  }, [availableLangs, activeLang]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-red-600 via-red-700 to-red-800 py-16 text-white relative overflow-hidden" data-testid="typing-hero">
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <Keyboard className="mx-auto mb-4" size={48} />
            <h1 className="text-4xl md:text-5xl font-bold mb-3">Free Typing Test Platform</h1>
            <p className="text-lg md:text-xl text-red-100 mb-6">
              Practice with real SSC CGL, CHSL, IBPS Clerk &amp; RRB NTPC exam patterns. Instant WPM &amp; accuracy results.
            </p>
            <div className="flex justify-center gap-2 flex-wrap">
              <Badge className="bg-white/20 text-white border-0 px-3 py-1.5">⏱ 10-minute tests</Badge>
              <Badge className="bg-white/20 text-white border-0 px-3 py-1.5">📊 Live WPM &amp; Accuracy</Badge>
              <Badge className="bg-white/20 text-white border-0 px-3 py-1.5">🇮🇳 English + Hindi</Badge>
              <Badge className="bg-white/20 text-white border-0 px-3 py-1.5">💯 100% Free</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Student Stats (if logged in) */}
      {stats && stats.total_attempts > 0 && (
        <section className="py-6 bg-white border-b" data-testid="typing-stats">
          <div className="container mx-auto px-4">
            <h2 className="text-lg font-bold mb-3 flex items-center">
              <TrendingUp className="mr-2 text-red-600" size={20} /> Your Progress
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Attempts" value={stats.total_attempts} />
              <StatCard label="Best WPM" value={stats.best_wpm} highlight />
              <StatCard label="Avg WPM" value={stats.avg_wpm} />
              <StatCard label="Tests Passed" value={`${stats.tests_passed}/${stats.total_attempts}`} />
            </div>
          </div>
        </section>
      )}

      {/* Tabs + List */}
      <section className="py-10">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Exam tabs */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="exam-tabs">
            {EXAMS.map(exam => (
              <button
                key={exam.id}
                data-testid={`exam-tab-${exam.id}`}
                onClick={() => setActiveExam(exam.id)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  activeExam === exam.id ? `${exam.accent} text-white shadow-lg` : 'bg-white border text-gray-700 hover:border-gray-400'
                }`}
              >
                {exam.name}
              </button>
            ))}
          </div>

          {/* Language tabs */}
          {availableLangs.length > 0 && (
            <div className="flex gap-2 mb-6" data-testid="lang-tabs">
              {availableLangs.map(lang => (
                <button
                  key={lang}
                  data-testid={`lang-tab-${lang}`}
                  onClick={() => setActiveLang(lang)}
                  className={`px-4 py-1.5 rounded-md text-sm transition-colors ${
                    activeLang === lang ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>
          )}

          {/* Passage cards */}
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
            </div>
          ) : filtered.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-gray-600">No tests available for this selection yet.</CardContent></Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4" data-testid="passage-list">
              {filtered.map((p, idx) => (
                <Card key={p.id} className="hover:shadow-lg transition-shadow" data-testid={`passage-${p.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <Badge variant="outline" className="mb-2">Test {idx + 1}</Badge>
                        <h3 className="text-lg font-bold">{p.title}</h3>
                      </div>
                      <Award className="text-yellow-500" size={20} />
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-4">
                      <span className="flex items-center"><Clock className="mr-1" size={12} /> {Math.round(p.duration_seconds / 60)} min</span>
                      <span className="flex items-center"><Target className="mr-1" size={12} /> Target: {p.target_wpm} WPM</span>
                      <span className="flex items-center"><Keyboard className="mr-1" size={12} /> {p.char_count} chars</span>
                    </div>
                    <p className="text-sm text-gray-500 italic line-clamp-2 mb-4">{p.content.slice(0, 120)}…</p>
                    <Link to={`/typing-test/${p.id}`}>
                      <Button data-testid={`start-${p.id}`} className="w-full bg-red-600 hover:bg-red-700">
                        <Play className="mr-2" size={14} /> Start Typing Test
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <GurujiChatWidget />
      <Footer />
    </div>
  );
};

const StatCard = ({ label, value, highlight }) => (
  <div className={`rounded-lg p-4 ${highlight ? 'bg-gradient-to-br from-red-50 to-orange-50 border border-red-200' : 'bg-gray-50 border'}`}>
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-xs text-gray-600">{label}</p>
  </div>
);

export default TypingTestList;
